/* eslint-disable complexity */
import { publicRoutes } from 'app/configs/routes';
import { isWindow } from 'app/utils';
import { refreshToken } from 'app/utils/refreshToken';
import { infoToast } from 'app/utils/toast';
import Axios from 'axios';
import queryString from 'query-string';
import BACKEND_URL from 'store/constants/urls';
import store from 'store/index';

export const getBackoffTime = (retryCount: number) => {
  const baseDelay = 1000; // 1 second
  const maxDelay = 5000; // 5 seconds
  const exponentialDelay = Math.min(
    maxDelay,
    baseDelay * Math.pow(2, retryCount)
  );
  const jitter = Math.random() * 0.3 * exponentialDelay; // 30% jitter
  return exponentialDelay + jitter;
};

const axios = Axios.create({
  baseURL: BACKEND_URL,
  paramsSerializer: params =>
    queryString.stringify(params, { arrayFormat: 'comma' }),
});

let refreshingToken: any = null;

axios.interceptors.request.use((request: any) => {
  const { user } = store.getState();

  let isAuthenticatedRoute;

  if (isWindow()) {
    const { pathname } = window.location;
    isAuthenticatedRoute = !publicRoutes.some(route =>
      pathname.includes(route.path)
    );
  } else {
    isAuthenticatedRoute = true;
  }

  if (isAuthenticatedRoute) {
    axios.defaults.withCredentials = true;
    request.headers.Authorization = `Bearer ${user.accessToken}`;
  } else {
    axios.defaults.withCredentials = false;
  }

  return request;
});

axios.interceptors.response.use(
  response => response,
  async error => {
    const { config } = error;

    if (config) {
      if (!config.url.includes('users/refresh_token')) {
        if (error?.response?.status === 403) {
          infoToast('You are not allowed to access this page.');
        } else if (
          error.response.data?.code === 'token_not_valid' &&
          !config._retry
        ) {
          config._retry = true;
          refreshingToken = refreshingToken ? refreshingToken : refreshToken();

          const token = await refreshingToken;
          refreshingToken = null;

          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          return axios(config);
        }
        // Handle 5XX errors with exponential backoff
        else if (
          error?.response?.status >= 500 ||
          error?.response?.status === 429
        ) {
          console.log('Retrying request due to server error or rate limit...');
          const retryCount = config._retryCount || 0;
          const maxRetries = 5;

          if (retryCount < maxRetries) {
            config._retryCount = retryCount + 1;
            const backoffTime = getBackoffTime(retryCount);

            // Wait for the calculated backoff time
            await new Promise(resolve => setTimeout(resolve, backoffTime));

            // Retry the request
            return axios(config);
          }
        }
      }
    }

    return Promise.reject(error);
  }
);

export { axios as axiosInstance };
